import { PlusOutlined } from '@ant-design/icons';
import {
  App,
  Button,
  Card,
  Dropdown,
  Empty,
  Flex,
  Select,
  Tag,
  theme,
  Tooltip,
  Typography,
} from 'antd';
import { ChevronDown, ChevronLeft, ChevronRight, CircleAlert } from 'lucide-react';
import {
  createContext,
  forwardRef,
  memo,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import ScheduledMealRowComponent, { type ScheduledMealRow } from './scheduled-meal-row.component';
import ConfirmModal from './scheduled-meals-confirm.modal';
import ScheduledMealsSummaryModal from './scheduled-meals-summary.modal';
import LoadingSpin from '../../../../components/loading-spin';
import { type CycleMenuResponseDto } from '../../../../dto/cycle-menu/cycle-menu-response-dto';
import { type ResponseDto } from '../../../../dto/ResponseDto';
import {
  ScheduledMealBatchAction,
  type ScheduledMealBatchItemRequestDto,
} from '../../../../dto/scheduled-meal/scheduled-meal-batch-dto';
import { type ScheduledMealResponseDto } from '../../../../dto/scheduled-meal/scheduled-meal-response-dto';
import { Meal } from '../../../../entities';
import useSecuredApiV2 from '../../../../hooks/use-secured-api-v2';
import { CYCLE_MENUS_ENDPOINT_URL } from '../../../../utils/endpoints';
import { LocalDate } from '../../../../utils/local-date';
import {
  MealCategoriesContext,
  MealItemsContext,
  MealsContext,
} from '../cycle-menu-schedules.page';

export interface MealCategoryItem {
  id: number;
  name: string;
  sortOrder: number;
  isGeneric: boolean;
  availableWeekDays: string;
}

export const ScheduledMealsDataContext = createContext<{
  sortedMealCategories: MealCategoryItem[];
  mealsByCategoryMap: Map<number, Array<{ value: number; label: string }>>;
  mealMap: Map<number, string>;
  mealItemOptions: Array<{ value: number; label: string }>;
  mealItemMap: Map<number, string>;
}>({
  sortedMealCategories: [],
  mealsByCategoryMap: new Map(),
  mealMap: new Map(),
  mealItemOptions: [],
  mealItemMap: new Map(),
});

interface PropsType {
  cycleMenu: CycleMenuResponseDto;
}

const calculateCurrentIteration = (
  startDate: LocalDate,
  endDate: LocalDate,
  repeatUntil: LocalDate | null
): number => {
  const cycleRange = endDate.diff(startDate, 'day') + 1;
  const dateNow = new LocalDate();
  const maxEnd = repeatUntil ?? endDate;
  if (dateNow.isBefore(startDate) || dateNow.isAfter(maxEnd)) {
    return 1;
  }
  return Math.floor(dateNow.diff(startDate, 'day') / cycleRange) + 1;
};

const ScheduledMealsPage = forwardRef(({ cycleMenu }: PropsType, ref) => {
  const { token } = theme.useToken();
  const { modal, notification } = App.useApp();
  const api = useSecuredApiV2();
  const navigate = useNavigate();

  const meals = useContext(MealsContext);
  const mealCategories = useContext(MealCategoriesContext);
  const mealItems = useContext(MealItemsContext);

  const [confirmSaveConfig, setConfirmSaveConfig] = useState<{
    closeCycleMenu: boolean;
    payload: ScheduledMealBatchItemRequestDto[];
  } | null>(null);
  const originalMealsRef = useRef<ScheduledMealResponseDto[]>([]);
  const pendingIterationRef = useRef<number | null>(null);

  const [isDirty, setIsDirty] = useState(false);
  const [isLoadingMeals, setIsLoadingMeals] = useState(true);
  const [isSwitchingDay, setIsSwitchingDay] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);

  // Iteration state: Record keyed by 'YYYY-MM-DD' containing ScheduledMealRow[]
  const [mealsByDay, setMealsByDay] = useState<Record<string, ScheduledMealRow[]>>({});
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);

  // Parse cycle menu date bounds
  const startDate = useMemo(() => new LocalDate(cycleMenu.startDate), [cycleMenu.startDate]);
  const endDate = useMemo(() => new LocalDate(cycleMenu.endDate), [cycleMenu.endDate]);
  const repeatUntil = useMemo(
    () => (cycleMenu.repeatUntil ? new LocalDate(cycleMenu.repeatUntil) : null),
    [cycleMenu.repeatUntil]
  );
  const cycleRange = useMemo(() => endDate.diff(startDate, 'day') + 1, [startDate, endDate]);

  const cycleMenuIterations = useMemo(() => {
    const maxEndDate = repeatUntil && repeatUntil.isAfter(endDate) ? repeatUntil : endDate;
    return Math.ceil((maxEndDate.diff(startDate, 'day') + 1) / cycleRange);
  }, [startDate, endDate, repeatUntil, cycleRange]);

  const [cycleMenuIteration, setCycleMenuIteration] = useState(() =>
    calculateCurrentIteration(startDate, endDate, repeatUntil)
  );

  useImperativeHandle(ref, () => ({
    getCurrentIteration: () => cycleMenuIteration,
  }));

  // Iteration boundary helpers
  const getIterationStart = useCallback(
    (iterationNumber: number): LocalDate => {
      return startDate.add((iterationNumber - 1) * cycleRange, 'day');
    },
    [startDate, cycleRange]
  );

  const getIterationEnd = useCallback(
    (iterationNumber: number): LocalDate => {
      const nominalEnd = endDate.add((iterationNumber - 1) * cycleRange, 'day');
      return repeatUntil && repeatUntil.isBefore(nominalEnd) ? repeatUntil : nominalEnd;
    },
    [endDate, repeatUntil, cycleRange]
  );

  const iterStart = useMemo(
    () => getIterationStart(cycleMenuIteration),
    [getIterationStart, cycleMenuIteration]
  );
  const iterEnd = useMemo(
    () => getIterationEnd(cycleMenuIteration),
    [getIterationEnd, cycleMenuIteration]
  );
  const daysInCurrentIteration = useMemo(
    () => iterEnd.diff(iterStart, 'day') + 1,
    [iterStart, iterEnd]
  );

  const dayDates = useMemo(() => {
    return Array.from({ length: daysInCurrentIteration }, (_, idx) => iterStart.add(idx, 'day'));
  }, [daysInCurrentIteration, iterStart]);

  // Pre-sort meal categories by sortOrder
  const sortedMealCategories: MealCategoryItem[] = useMemo(() => {
    return (mealCategories.data ?? [])
      .map((mc) => ({
        id: Number(mc.id),
        name: mc.name,
        sortOrder: mc.sortOrder ?? 127,
        isGeneric: Boolean(mc.isGeneric),
        availableWeekDays: mc.availableWeekDays ?? '1111111',
      }))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [mealCategories.data]);

  // Pre-index meal names for O(1) label lookup
  const mealMap = useMemo(() => {
    const map = new Map<number, string>();
    meals.data?.forEach((meal) => {
      map.set(Number(meal.id), Object.assign(new Meal(), meal).displayName());
    });
    return map;
  }, [meals.data]);

  // Pre-index meal items for O(1) label lookup
  const mealItemMap = useMemo(() => {
    const map = new Map<number, string>();
    mealItems.data?.forEach((mi: any) => {
      map.set(Number(mi.id), mi.name);
    });
    return map;
  }, [mealItems.data]);

  // Pre-index meals by category into an O(1) Map
  const mealsByCategoryMap = useMemo(() => {
    const map = new Map<number, Array<{ value: number; label: string }>>();
    meals.data?.forEach((meal) => {
      const option = {
        value: Number(meal.id),
        label: Object.assign(new Meal(), meal).displayName(),
      };
      meal.definedMealCategories?.forEach((cat) => {
        const catId = Number(cat.id);
        const list = map.get(catId) ?? [];
        list.push(option);
        map.set(catId, list);
      });
    });
    return map;
  }, [meals.data]);

  // Pre-index meal items for dropdown selects into a stable array
  const mealItemOptions = useMemo(() => {
    return (mealItems.data ?? []).map((mi: any) => ({
      value: Number(mi.id),
      label: mi.name,
    }));
  }, [mealItems.data]);

  const contextValue = useMemo(
    () => ({
      sortedMealCategories,
      mealsByCategoryMap,
      mealMap,
      mealItemOptions,
      mealItemMap,
    }),
    [sortedMealCategories, mealsByCategoryMap, mealMap, mealItemOptions, mealItemMap]
  );

  // Load scheduled meals for the given iteration
  const fetchScheduledMeals = useCallback(
    (iteration: number) => {
      if (!cycleMenu?.id) return;
      setIsLoadingMeals(true);

      const itStart = getIterationStart(iteration);
      const itEnd = getIterationEnd(iteration);
      const daysCount = itEnd.diff(itStart, 'day') + 1;

      const sub = api
        .get(`${CYCLE_MENUS_ENDPOINT_URL}/${cycleMenu.id}/scheduled-meals`, { iteration }, {})
        .subscribe({
          next: (res: ResponseDto<ScheduledMealResponseDto[]>) => {
            const rawMeals = res.data ?? [];
            originalMealsRef.current = rawMeals;

            const nextMealsByDay: Record<string, ScheduledMealRow[]> = {};
            for (let dayNo = 0; dayNo < daysCount; dayNo++) {
              const currentDay = itStart.add(dayNo, 'day');
              const dayStr = currentDay.format('YYYY-MM-DD');

              const dayMeals: ScheduledMealRow[] = rawMeals
                .filter((sm: ScheduledMealResponseDto) => sm.day === dayStr)
                .map((sm: ScheduledMealResponseDto) => ({
                  uid: sm.id ? `db-${sm.id}` : `client-${Math.random()}`,
                  id: sm.id,
                  day: sm.day,
                  mealCategory: sm.mealCategory?.id ? Number(sm.mealCategory.id) : undefined,
                  meal: sm.meal?.id ? Number(sm.meal.id) : undefined,
                  extraMealItemId1: sm.extraMealItem1?.id,
                  extraMealItemId2: sm.extraMealItem2?.id,
                  extraMealItemServeType1: sm.extraMealItemServeType1 ?? null,
                  extraMealItemServeType2: sm.extraMealItemServeType2 ?? null,
                  price: sm.price,
                  order: sm.mealCategory?.sortOrder ?? 127,
                  isException: Boolean(sm.isException),
                }))
                .sort((a, b) => a.order - b.order);

              nextMealsByDay[dayStr] = dayMeals;
            }

            setMealsByDay(nextMealsByDay);
            setIsDirty(false);
            setIsLoadingMeals(false);
          },
          error: (err: any) => {
            notification.error({
              message: '',
              description:
                err?.detail ??
                err?.error ??
                err?.message ??
                'Failed to load scheduled meals for iteration',
            });
            setIsLoadingMeals(false);
          },
        });

      return () => {
        sub.unsubscribe();
      };
    },
    [cycleMenu.id]
  );

  // Fetch when iteration changes or cycleMenu mounts (resets active day to Day 1)
  useEffect(() => {
    setSelectedDayIndex(0);
    const cancelSub = fetchScheduledMeals(cycleMenuIteration);
    return () => {
      cancelSub?.();
    };
  }, [fetchScheduledMeals, cycleMenuIteration]);

  // Current active day information
  const currentDayDate = useMemo(() => {
    return dayDates[selectedDayIndex] ?? iterStart;
  }, [dayDates, selectedDayIndex, iterStart]);

  const currentDayStr = useMemo(() => {
    return currentDayDate.format('YYYY-MM-DD');
  }, [currentDayDate]);

  const activeRows = useMemo(() => {
    return mealsByDay[currentDayStr] ?? [];
  }, [mealsByDay, currentDayStr]);

  // Active day's selected category IDs (for filtering dropdown options)
  const selectedCategoryIds = useMemo(() => {
    return new Set(
      activeRows
        .map((r) => (r.mealCategory ? Number(r.mealCategory) : null))
        .filter((id): id is number => id !== null)
    );
  }, [activeRows]);

  // Handlers for active day rows
  const handleUpdateRow = useCallback(
    (rowIndex: number, partial: Partial<ScheduledMealRow>) => {
      setMealsByDay((prev) => {
        const rows = [...(prev[currentDayStr] ?? [])];
        rows[rowIndex] = { ...rows[rowIndex], ...partial };
        return { ...prev, [currentDayStr]: rows };
      });
      setIsDirty(true);
    },
    [currentDayStr]
  );

  const handleDeleteRow = useCallback(
    (rowIndex: number) => {
      setMealsByDay((prev) => {
        const rows = (prev[currentDayStr] ?? []).filter((_, idx) => idx !== rowIndex);
        return { ...prev, [currentDayStr]: rows };
      });
      setIsDirty(true);
    },
    [currentDayStr]
  );

  const handleAddMeal = useCallback(() => {
    setMealsByDay((prev) => {
      const rows = [...(prev[currentDayStr] ?? [])];
      rows.push({
        uid: `client-${Date.now()}-${Math.random()}`,
        day: currentDayStr,
        order: 127,
        isException: false,
        price: 0,
      });
      return { ...prev, [currentDayStr]: rows };
    });
    setIsDirty(true);
  }, [currentDayStr]);

  // Apply price to category across the entire iteration
  const applyPriceToCategory = useCallback(
    (mealCategoryId: number, price: number): void => {
      setMealsByDay((prev) => {
        const updated: Record<string, ScheduledMealRow[]> = {};
        Object.entries(prev).forEach(([dayStr, rows]) => {
          updated[dayStr] = rows.map((r) => {
            if (r.mealCategory === mealCategoryId && !r.isException) {
              return { ...r, price };
            }
            return r;
          });
        });
        return updated;
      });
      setIsDirty(true);
      notification.success({
        message: '',
        description: 'Applied price to all non-exception meals of this category across iteration.',
      });
    },
    [notification]
  );

  // Repeat meal to following days in current iteration
  const applyMealToDays = useCallback(
    async (sourceMeal: ScheduledMealRow, startDayIndex: number): Promise<void> => {
      setMealsByDay((prev) => {
        const updated = { ...prev };
        const targetCategory = sortedMealCategories.find((mc) => mc.id === sourceMeal.mealCategory);

        for (let dayIdx = startDayIndex + 1; dayIdx < daysInCurrentIteration; dayIdx++) {
          const dayDate = iterStart.add(dayIdx, 'day');
          const isAvailable = targetCategory?.availableWeekDays?.[dayDate.day()] === '1';
          if (!isAvailable) continue;

          const dayStr = dayDate.format('YYYY-MM-DD');
          const existingRows = [...(updated[dayStr] ?? [])];
          const matchIdx = existingRows.findIndex(
            (r) => r.mealCategory === sourceMeal.mealCategory
          );

          if (matchIdx >= 0) {
            const existing = existingRows[matchIdx];
            if (existing.isException) continue;
            existingRows[matchIdx] = {
              ...sourceMeal,
              uid: existing.uid,
              id: existing.id,
              day: dayStr,
              isException: false,
            };
          } else {
            existingRows.push({
              ...sourceMeal,
              uid: `client-${Date.now()}-${Math.random()}`,
              id: undefined,
              day: dayStr,
              isException: false,
            });
          }
          updated[dayStr] = existingRows;
        }
        return updated;
      });
      setIsDirty(true);
      notification.success({
        message: '',
        description: 'Repeated meal to remaining days in iteration.',
      });
    },
    [daysInCurrentIteration, iterStart, sortedMealCategories, notification]
  );

  // Toggle all exceptions for active day
  const allAreExceptions = activeRows.length > 0 && activeRows.every((r) => r.isException);
  const someAreExceptions = activeRows.length > 0 && activeRows.some((r) => r.isException);

  const handleToggleAllExceptions = useCallback(() => {
    setMealsByDay((prev) => {
      const rows = [...(prev[currentDayStr] ?? [])];
      const nextRows = rows.map((r) => ({
        ...r,
        isException: !allAreExceptions,
      }));
      return { ...prev, [currentDayStr]: nextRows };
    });
    setIsDirty(true);
  }, [currentDayStr, allAreExceptions]);

  // Validation function before saving
  const validateIterationMeals = (): boolean => {
    const invalidDays: Array<{ dayIdx: number; dateFormatted: string }> = [];

    for (let dayIdx = 0; dayIdx < daysInCurrentIteration; dayIdx++) {
      const dDate = iterStart.add(dayIdx, 'day');
      const dStr = dDate.format('YYYY-MM-DD');
      const rows = mealsByDay[dStr] ?? [];

      let dayHasError = false;
      for (let rIdx = 0; rIdx < rows.length; rIdx++) {
        const row = rows[rIdx];
        if (!row.mealCategory) {
          dayHasError = true;
          break;
        }

        const cat = sortedMealCategories.find((c) => c.id === row.mealCategory);
        if (!cat?.isGeneric && !row.meal) {
          dayHasError = true;
          break;
        }

        if (row.extraMealItemId1 && !row.extraMealItemServeType1) {
          dayHasError = true;
          break;
        }

        if (!row.extraMealItemId1 && row.extraMealItemServeType1) {
          dayHasError = true;
          break;
        }

        if (row.extraMealItemId2 && !row.extraMealItemServeType2) {
          dayHasError = true;
          break;
        }

        if (!row.extraMealItemId2 && row.extraMealItemServeType2) {
          dayHasError = true;
          break;
        }

        if (row.price === undefined || row.price === null) {
          dayHasError = true;
          break;
        }
      }

      if (dayHasError) {
        invalidDays.push({
          dayIdx,
          dateFormatted: `(${dDate.format('MMM DD, YYYY')})`,
        });
      }
    }

    if (invalidDays.length > 0) {
      notification.error({
        message: 'Validation Error',
        description: `Please complete the required fields in days ${invalidDays
          .map((d) => d.dateFormatted)
          .join(', ')}.`,
      });
      return false;
    }

    return true;
  };

  // Compute diffs and initiate save
  const onInitiateSave = async (saveAndClose: boolean): Promise<void> => {
    if (!validateIterationMeals()) {
      return;
    }

    const currentRows: ScheduledMealRow[] = [];
    Object.values(mealsByDay).forEach((dayRows) => {
      currentRows.push(...dayRows);
    });

    const originalMeals = originalMealsRef.current;

    // 1. Identify deleted records
    const toDelete: ScheduledMealBatchItemRequestDto[] = originalMeals
      .filter((orig) => !currentRows.some((r) => r.id && r.id === orig.id))
      .map((orig) => ({
        action: ScheduledMealBatchAction.DELETE,
        id: orig.id,
        day: orig.day,
        mealCategoryId: orig.mealCategory!.id,
        price: orig.price,
        isException: Boolean(orig.isException),
      }));

    // 2. Identify new or changed records
    const toUpsert: ScheduledMealBatchItemRequestDto[] = [];
    for (const row of currentRows) {
      const cat = sortedMealCategories.find((c) => c.id === Number(row.mealCategory));
      const mealIdVal = cat?.isGeneric ? null : row.meal ? Number(row.meal) : null;

      if (!row.id) {
        toUpsert.push({
          action: ScheduledMealBatchAction.UPSERT,
          id: null,
          day: row.day,
          mealCategoryId: Number(row.mealCategory),
          mealId: mealIdVal,
          price: Number(row.price ?? 0),
          isException: Boolean(row.isException),
          extraMealItemId1: row.extraMealItemId1 ? Number(row.extraMealItemId1) : null,
          extraMealItemId2: row.extraMealItemId2 ? Number(row.extraMealItemId2) : null,
          extraMealItemServeType1: row.extraMealItemServeType1 || null,
          extraMealItemServeType2: row.extraMealItemServeType2 || null,
        });
      } else {
        const orig = originalMeals.find((o) => o.id === row.id);
        const isChanged =
          !orig ||
          Number(orig.mealCategory?.id) !== Number(row.mealCategory) ||
          (orig.meal?.id ?? null) !== mealIdVal ||
          Number(orig.price) !== Number(row.price) ||
          Boolean(orig.isException) !== Boolean(row.isException) ||
          (orig.extraMealItem1?.id ?? null) !==
            (row.extraMealItemId1 ? Number(row.extraMealItemId1) : null) ||
          (orig.extraMealItem2?.id ?? null) !==
            (row.extraMealItemId2 ? Number(row.extraMealItemId2) : null) ||
          (orig.extraMealItemServeType1 ?? null) !== (row.extraMealItemServeType1 || null) ||
          (orig.extraMealItemServeType2 ?? null) !== (row.extraMealItemServeType2 || null);

        if (isChanged) {
          toUpsert.push({
            action: ScheduledMealBatchAction.UPSERT,
            id: row.id,
            day: row.day,
            mealCategoryId: Number(row.mealCategory),
            mealId: mealIdVal,
            price: Number(row.price ?? 0),
            isException: Boolean(row.isException),
            extraMealItemId1: row.extraMealItemId1 ? Number(row.extraMealItemId1) : null,
            extraMealItemId2: row.extraMealItemId2 ? Number(row.extraMealItemId2) : null,
            extraMealItemServeType1: row.extraMealItemServeType1 || null,
            extraMealItemServeType2: row.extraMealItemServeType2 || null,
          });
        }
      }
    }

    if (toUpsert.length === 0 && toDelete.length === 0) {
      notification.info({
        message: '',
        description: 'No changes detected to save.',
      });
      setIsDirty(false);
      if (saveAndClose) {
        navigate('/cycle-menus');
      } else if (pendingIterationRef.current !== null) {
        const nextIter = pendingIterationRef.current;
        pendingIterationRef.current = null;
        setCycleMenuIteration(nextIter);
      }
      return;
    }

    const payload = [...toUpsert, ...toDelete];

    setConfirmSaveConfig({
      closeCycleMenu: saveAndClose,
      payload,
    });
  };

  // Perform backend batch save request
  const onConfirmSave = (saveToNextIterations: boolean): void => {
    if (!confirmSaveConfig) return;
    const { closeCycleMenu, payload } = confirmSaveConfig;
    const iteration = cycleMenuIteration;

    setIsSaving(true);

    api
      .post(
        `${CYCLE_MENUS_ENDPOINT_URL}/${cycleMenu.id}/scheduled-meals`,
        payload,
        {
          iteration,
          saveToNextIterations,
        },
        {}
      )
      .subscribe({
        next: () => {
          setConfirmSaveConfig(null);
          setIsSaving(false);
          setIsDirty(false);
          notification.success({
            message: '',
            description: 'Scheduled meals saved successfully',
          });

          if (closeCycleMenu) {
            navigate('/cycle-menus');
          } else if (pendingIterationRef.current !== null) {
            const nextIter = pendingIterationRef.current;
            pendingIterationRef.current = null;
            setCycleMenuIteration(nextIter);
          } else {
            fetchScheduledMeals(iteration);
          }
        },
        error: (err: any) => {
          setConfirmSaveConfig(null);
          setIsSaving(false);
          notification.error({
            message: err?.title ?? '',
            description:
              err?.detail ??
              err?.message ??
              (typeof err === 'string' ? err : 'Failed to save scheduled meals'),
          });
        },
      });
  };

  // Discard changes
  const onDiscardChanges = (): void => {
    void modal.confirm({
      width: '50%',
      title: 'Confirm discard',
      centered: true,
      icon: <CircleAlert />,
      content: (
        <Typography.Text>
          Are you sure you want to discard current modifications for <b>{cycleMenu.name}</b>?
        </Typography.Text>
      ),
      okText: 'Yes',
      okType: 'primary',
      cancelText: 'No',
      onOk: () => {
        fetchScheduledMeals(cycleMenuIteration);
      },
    });
  };

  // Iteration switch handler with dirty check guard
  const onIterationSelectChange = (newIteration: number): void => {
    if (newIteration === cycleMenuIteration) return;

    if (!isDirty) {
      setIsLoadingMeals(true);
      setCycleMenuIteration(newIteration);
      return;
    }

    void modal.confirm({
      title: 'Unsaved Changes',
      icon: <CircleAlert />,
      content: `You have unsaved changes in Iteration ${cycleMenuIteration}. Would you like to save them before switching?`,
      okText: 'Save & Switch',
      cancelText: 'Discard & Switch',
      maskClosable: false,
      onOk: () => {
        pendingIterationRef.current = newIteration;
        void onInitiateSave(false);
      },
      onCancel: () => {
        pendingIterationRef.current = null;
        setIsDirty(false);
        setIsLoadingMeals(true);
        setCycleMenuIteration(newIteration);
      },
    });
  };

  const isToday = currentDayDate.isSame(new LocalDate(), 'd');
  const isLastRepeatDate =
    cycleMenu.repeatUntil && currentDayDate.isSame(new LocalDate(cycleMenu.repeatUntil), 'd');

  const handleDayChange = useCallback(
    (newIdx: number) => {
      if (newIdx === selectedDayIndex) return;
      setIsSwitchingDay(true);
      setTimeout(() => {
        setSelectedDayIndex(newIdx);
        requestAnimationFrame(() => {
          setIsSwitchingDay(false);
        });
      }, 20);
    },
    [selectedDayIndex]
  );

  const dayOptions = useMemo(() => {
    return dayDates.map((dDate, idx) => {
      const dayStr = dDate.format('YYYY-MM-DD');
      const mealCount = (mealsByDay[dayStr] ?? []).length;
      const isDayToday = dDate.isSame(new LocalDate(), 'd');
      return {
        value: idx,
        label: (
          <Flex justify="space-between" align="center" style={{ width: '100%' }}>
            <span>
              <b>Day #{idx + 1}</b> • {dDate.format('ddd, MMM DD')}
              {isDayToday ? ' (Today)' : ''}
            </span>
            <Tag
              className="day-meals-count-tag"
              bordered
              style={{
                marginInline: 0,
                marginInlineStart: 8,
                borderRadius: 10,
                fontSize: 12,
              }}
            >
              {mealCount} {mealCount === 1 ? 'meal' : 'meals'}
            </Tag>
          </Flex>
        ),
      };
    });
  }, [dayDates, mealsByDay]);

  return (
    <ScheduledMealsDataContext.Provider value={contextValue}>
      <Flex gap="middle" vertical>
        {/* Header with Iteration & Day Selectors */}
        <Flex gap="middle" justify="space-between" align="start">
          <Flex vertical gap={4}>
            <Typography.Title level={3} style={{ margin: 0 }}>
              Scheduled Meals
            </Typography.Title>
            <Typography.Text type="secondary">
              Configure meals day by day. All changes in the iteration are saved together.
            </Typography.Text>
          </Flex>

          {/* Navigation Controls Group */}
          <Flex vertical gap="small" align="end" style={{ minWidth: 540 }}>
            {/* Iteration Selector & Iteration Summary Button */}
            <Flex align="center" gap="small" style={{ width: '100%' }}>
              <Typography.Text strong style={{ minWidth: 65 }}>
                Iteration:
              </Typography.Text>
              <Select
                style={{ flex: 1 }}
                value={cycleMenuIteration}
                options={Array.from({ length: cycleMenuIterations }, (_, i) => i + 1).map((el) => ({
                  label: (
                    <span>
                      <b>Iteration {el}</b>: {getIterationStart(el).format('MMM DD, YYYY')} -{' '}
                      {getIterationEnd(el).format('MMM DD, YYYY')}
                    </span>
                  ),
                  value: el,
                }))}
                onChange={onIterationSelectChange}
                disabled={isLoadingMeals || isSaving}
              />
              <Button
                onClick={() => {
                  setIsSummaryModalOpen(true);
                }}
                disabled={isLoadingMeals}
              >
                Summary
              </Button>
            </Flex>

            {/* Day Selector with Prev/Next buttons */}
            <Flex align="center" gap="small" style={{ width: '100%' }}>
              <Typography.Text strong style={{ minWidth: 65 }}>
                Day:
              </Typography.Text>
              <Button
                icon={<ChevronLeft size={16} />}
                disabled={isLoadingMeals || isSaving || isSwitchingDay || selectedDayIndex <= 0}
                onClick={() => {
                  handleDayChange(Math.max(0, selectedDayIndex - 1));
                }}
                title="Previous Day"
              />
              <Select
                style={{ flex: 1 }}
                showSearch
                filterOption={(input, option) => {
                  const dDate = dayDates[option?.value as number];
                  if (!dDate) return false;
                  const searchStr = `day ${Number(option?.value) + 1} ${dDate.format(
                    'ddd MMM DD YYYY'
                  )}`.toLowerCase();
                  return searchStr.includes(input.toLowerCase());
                }}
                disabled={isLoadingMeals || isSaving || isSwitchingDay}
                value={selectedDayIndex}
                onChange={handleDayChange}
                options={dayOptions}
              />
              <Button
                icon={<ChevronRight size={16} />}
                disabled={
                  isLoadingMeals ||
                  isSaving ||
                  isSwitchingDay ||
                  selectedDayIndex >= daysInCurrentIteration - 1
                }
                onClick={() => {
                  handleDayChange(Math.min(daysInCurrentIteration - 1, selectedDayIndex + 1));
                }}
                title="Next Day"
              />
            </Flex>
          </Flex>
        </Flex>

        {/* Active Day Section */}
        {isLoadingMeals ? (
          <Flex justify="center" align="center" style={{ minHeight: 280 }}>
            <LoadingSpin />
          </Flex>
        ) : (
          <Card
            bordered
            style={{
              width: '100%',
              marginBlockEnd: token.paddingXL * 6,
              boxShadow: token.boxShadowTertiary,
            }}
          >
            {/* Day Card Header */}
            <Flex
              justify="space-between"
              align="center"
              style={{
                paddingBlockEnd: token.paddingMD,
                borderBlockEnd: `1px solid ${token.colorBorderSecondary}`,
                marginBlockEnd: token.paddingMD,
              }}
            >
              <Flex align="center" gap="small" wrap="wrap">
                <Typography.Title level={4} style={{ margin: 0 }}>
                  Day #{selectedDayIndex + 1} • {currentDayDate.format('dddd, MMMM D, YYYY')}
                </Typography.Title>
                {isToday && (
                  <Tag color={token.colorPrimaryHover} style={{ textTransform: 'uppercase' }}>
                    Today
                  </Tag>
                )}
                {isLastRepeatDate && (
                  <Tag color="pink" style={{ textTransform: 'uppercase' }}>
                    Last repeat date
                  </Tag>
                )}
                <Tag color="blue">
                  {activeRows.length}{' '}
                  {activeRows.length === 1 ? 'meal scheduled' : 'meals scheduled'}
                </Tag>
              </Flex>

              {/* Mark All Exceptions Button */}
              <Tooltip
                placement="left"
                title={
                  allAreExceptions
                    ? 'Unmark all meals as Exception for this day'
                    : 'Mark all meals as Exception for this day'
                }
              >
                <span>
                  <Button
                    shape="circle"
                    disabled={activeRows.length === 0}
                    style={{
                      ...(allAreExceptions
                        ? { backgroundColor: token.colorPrimary, color: 'white' }
                        : someAreExceptions
                        ? { backgroundColor: 'gray', color: 'white' }
                        : {}),
                    }}
                    onClick={handleToggleAllExceptions}
                  >
                    <b>E</b>
                  </Button>
                </span>
              </Tooltip>
            </Flex>

            {/* Day Meal Rows */}
            {isSwitchingDay ? (
              <Flex justify="center" align="center" style={{ minHeight: 280 }}>
                <LoadingSpin />
              </Flex>
            ) : (
              <Flex vertical gap="middle">
                {activeRows.length === 0 ? (
                  <Empty
                    description="No meals scheduled for this day"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ paddingBlock: token.paddingMD }}
                  />
                ) : (
                  <>
                    {/* Column Headers */}
                    <Flex
                      align="center"
                      gap="small"
                      style={{
                        width: '100%',
                        paddingBlockEnd: token.paddingXXS,
                        marginBlockEnd: token.paddingXXS,
                      }}
                    >
                      {/* Spacer matching delete button width */}
                      <div style={{ width: 24, flexShrink: 0 }} />

                      {/* Header columns matching the widths of Space.Compact items */}
                      <Flex style={{ flex: 1 }} gap={0}>
                        <div style={{ flex: 2, minWidth: 120, paddingInlineEnd: 4 }}>
                          <Typography.Text strong>Meal Category</Typography.Text>
                        </div>
                        <div style={{ flex: 2.5, minWidth: 140, paddingInlineEnd: 4 }}>
                          <Typography.Text strong>Meal</Typography.Text>
                        </div>
                        <div style={{ flex: 2, minWidth: 120, paddingInlineEnd: 4 }}>
                          <Typography.Text strong>Extra meal item #1</Typography.Text>
                        </div>
                        <div style={{ flex: 1.5, minWidth: 100, paddingInlineEnd: 4 }}>
                          <Typography.Text strong>Extra #1 serve type</Typography.Text>
                        </div>
                        <div style={{ flex: 2, minWidth: 120, paddingInlineEnd: 4 }}>
                          <Typography.Text strong>Extra meal item #2</Typography.Text>
                        </div>
                        <div style={{ flex: 1.5, minWidth: 100, paddingInlineEnd: 4 }}>
                          <Typography.Text strong>Extra #2 serve type</Typography.Text>
                        </div>
                        <div style={{ width: 90, flexShrink: 0, paddingInlineEnd: 4 }}>
                          <Typography.Text strong>Price</Typography.Text>
                        </div>
                        {/* Spacer for Repeat button (32px) and Exception button (32px) */}
                        <div style={{ width: 64, flexShrink: 0 }} />
                      </Flex>
                    </Flex>

                    {activeRows.map((row, idx) => (
                      <ScheduledMealRowComponent
                        key={row.uid}
                        index={idx}
                        row={row}
                        dayDate={currentDayDate}
                        indexInIteration={selectedDayIndex}
                        selectedCategoryIds={selectedCategoryIds}
                        onUpdateRow={handleUpdateRow}
                        onDeleteRow={handleDeleteRow}
                        applyPriceToCategory={applyPriceToCategory}
                        applyMealToDays={applyMealToDays}
                        isLoading={isLoadingMeals || isSaving}
                      />
                    ))}
                  </>
                )}

                {/* Add Meal Button */}
                <Button
                  type="dashed"
                  block
                  icon={<PlusOutlined />}
                  onClick={handleAddMeal}
                  disabled={isLoadingMeals || isSaving}
                  style={{ height: token.controlHeightLG, marginBlockStart: token.paddingXS }}
                >
                  Add Meal
                </Button>
              </Flex>
            )}
          </Card>
        )}

        {/* Floating Action Controls */}
        <Flex
          className="floating"
          gap="small"
          style={{
            position: 'absolute',
            bottom: 0,
            flexDirection: 'row-reverse',
            width: `calc(100% - ${token.paddingMD * 2}px)`,
            paddingBlockStart: token.paddingXL * 2.5,
            paddingBlockEnd: token.paddingXL * 2,
            paddingInline: token.paddingXL,
            pointerEvents: 'none',
          }}
        >
          <Dropdown
            disabled={isLoadingMeals || isSaving}
            menu={{
              items: [
                {
                  label: 'Save',
                  key: 'save',
                  onClick: () => {
                    void onInitiateSave(false);
                  },
                },
                {
                  label: 'Save and Close',
                  key: 'save-close',
                  onClick: () => {
                    void onInitiateSave(true);
                  },
                },
              ],
            }}
          >
            <span style={{ pointerEvents: 'auto' }}>
              <Button
                size="large"
                type="primary"
                icon={<ChevronDown />}
                disabled={isLoadingMeals || isSaving}
                loading={isSaving}
              >
                Save
              </Button>
            </span>
          </Dropdown>
          <Button
            onClick={onDiscardChanges}
            size="large"
            disabled={isLoadingMeals || isSaving || !isDirty}
            style={{ pointerEvents: 'auto' }}
          >
            Discard Changes
          </Button>
        </Flex>

        <ConfirmModal
          open={confirmSaveConfig !== null}
          confirmLoading={isSaving}
          onConfirm={onConfirmSave}
          onCancel={() => {
            setConfirmSaveConfig(null);
          }}
        />

        <ScheduledMealsSummaryModal
          open={isSummaryModalOpen}
          onClose={() => {
            setIsSummaryModalOpen(false);
          }}
          iteration={cycleMenuIteration}
          iterationStart={iterStart}
          iterationEnd={iterEnd}
          dayDates={dayDates}
          scheduledMeals={originalMealsRef.current}
        />
      </Flex>
    </ScheduledMealsDataContext.Provider>
  );
});

ScheduledMealsPage.displayName = 'ScheduledMealsPage';
export default memo(ScheduledMealsPage);
